"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

type ScrollToHashProps = {
  id: string;
  behavior?: ScrollBehavior;
};

const getHashTargetId = (hash: string) => {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  return decodeURIComponent(raw.split("?")[0] || "").trim();
};

/** Scrolls to an element when the URL hash matches, including after Next.js client navigations. */
const ScrollToHash = ({ id, behavior = "smooth" }: ScrollToHashProps) => {
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window === "undefined") return;
  
    let cancelled = false;
    const timeouts: number[] = [];
  
    const scrollToTarget = () => {
      if (getHashTargetId(window.location.hash) !== id) return;
      const el = document.getElementById(id);
      if (!el) return false;
      el.scrollIntoView({ behavior, block: "start" });
      return true;
    };
  
    // retry: Next often scrolls to top after first paint
    [0, 50, 150, 350, 700].forEach((ms) => {
      const t = window.setTimeout(() => {
        if (!cancelled) scrollToTarget();
      }, ms);
      timeouts.push(t);
    });
  
    window.addEventListener("hashchange", scrollToTarget);
    window.addEventListener("popstate", scrollToTarget);
  
    return () => {
      cancelled = true;
      timeouts.forEach(clearTimeout);
      window.removeEventListener("hashchange", scrollToTarget);
      window.removeEventListener("popstate", scrollToTarget);
    };
  }, [pathname, id, behavior]);

  return null;
};

export default ScrollToHash;
